import styled from 'styled-components';
import { GlobalSearch } from './GlobalSearch';
import { DangerButton } from './ui';
import type { AuthUser, SelectedGlobalContext, ThemeMode } from '../types';

const Bar = styled.header`
  position: sticky;
  top: 0;
  z-index: 50;
  grid-column: 2;
  grid-row: 1;
  min-height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 28px;
  background: ${p => p.theme.bg};
  color: ${p => p.theme.text};
  border-bottom: 1px solid ${p => p.theme.border};
  backdrop-filter: blur(16px);

  @media (max-width: 920px) {
    grid-column: 1;
    grid-row: 2;
  }

  @media (max-width: 760px) {
    min-height: 0;
    align-items: flex-start;
    flex-direction: column;
    padding: 14px 16px;
  }
`;

const Scope = styled.div`
  flex: 0 1 auto;
  min-width: 0;
`;

const Trail = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  color: ${p => p.theme.muted};
  font-size: 13px;
  font-weight: 700;
`;

const Current = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${p => p.theme.text};
  font-weight: 900;
`;

const Chevron = styled.span`
  color: ${p => p.theme.muted};
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  color: ${p => p.theme.muted};
  font-size: 12px;
`;

const Directory = styled.code`
  max-width: min(32vw, 520px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 3px 8px;
  border-radius: 999px;
  background: ${p => p.theme.primarySoft};
  color: ${p => p.theme.primary};
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 11px;
`;

const ToolbarButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid ${p => p.theme.border};
  border-radius: 10px;
  background: ${p => p.theme.surface};
  color: ${p => p.theme.text};
  font-size: 13px;
  font-weight: 800;
  transition: background .15s ease, border-color .15s ease;

  &:hover {
    background: ${p => p.theme.primarySoft};
    border-color: ${p => p.theme.primary};
  }
`;

const Actions = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;

  ${DangerButton} {
    border-radius: 10px;
    padding: 8px 12px;
  }
`;

const VisuallyHiddenTitle = styled.h1`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

type Props = {
  user: AuthUser;
  directory: string;
  themeMode: ThemeMode;
  onSelectContext: (context: SelectedGlobalContext) => void;
  onToggleTheme: () => void;
  onLogout: () => void;
};

export function Topbar({ user, directory, themeMode, onSelectContext, onToggleTheme, onLogout }: Props) {
  const label = user.name || user.username || user.email || 'PocketBase user';

  return (
    <Bar>
      <VisuallyHiddenTitle>BlanketChat</VisuallyHiddenTitle>
      <Scope>
        <Trail>
          <span>Console</span>
          <Chevron>&gt;</Chevron>
          <Current title={label}>{label}</Current>
        </Trail>
        <Meta>
          <span>Directory</span>
          <Directory title={directory}>{directory}</Directory>
        </Meta>
      </Scope>
      <GlobalSearch onSelectContext={onSelectContext} />
      <Actions>
        <ToolbarButton type="button" onClick={onToggleTheme}>{themeMode === 'light' ? 'Dark mode' : 'Light mode'}</ToolbarButton>
        <DangerButton type="button" onClick={onLogout}>Logout</DangerButton>
      </Actions>
    </Bar>
  );
}
